import { Box, Button, Portal, Text } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'

interface FloatingDebuggerProps {
  onClick: () => void
}

export const FloatingDebugger: React.FC<FloatingDebuggerProps> = ({
  onClick
}) => {
  // Get initial position from localStorage or default to bottom-left
  const getInitialPosition = () => {
    try {
      const savedPosition = localStorage.getItem('floating-debugger-position')
      if (savedPosition) {
        return JSON.parse(savedPosition)
      }
    } catch (error) {
      console.warn('Failed to load debugger position from localStorage:', error)
    }
    return { x: 20, y: window.innerHeight - 80 }
  }

  const [position, setPosition] = useState(getInitialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const [hasDragged, setHasDragged] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 })
  const buttonRef = useRef<HTMLDivElement>(null)

  // Save position to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        'floating-debugger-position',
        JSON.stringify(position)
      )
    } catch (error) {
      console.warn('Failed to save debugger position to localStorage:', error)
    }
  }, [position])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault() // Prevent default to avoid interference with click
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
      setDragStartPosition({ x: e.clientX, y: e.clientY })
      setIsDragging(true)
      setHasDragged(false)
    }
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      // Only consider it a drag if the mouse has moved more than 5 pixels
      const deltaX = Math.abs(e.clientX - dragStartPosition.x)
      const deltaY = Math.abs(e.clientY - dragStartPosition.y)
      const hasMovedEnough = deltaX > 5 || deltaY > 5

      if (hasMovedEnough) {
        setHasDragged(true)
      }

      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y

      // Keep button within viewport bounds
      const maxX = window.innerWidth - 60
      const maxY = window.innerHeight - 60

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Only trigger onClick if the user didn't drag significantly
    if (!hasDragged && onClick) {
      onClick()
    }
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragOffset, dragStartPosition])

  return (
    <Portal>
      <Box
        ref={buttonRef}
        position="fixed"
        left={`${position.x}px`}
        top={`${position.y}px`}
        zIndex={9999}
        cursor={isDragging ? 'grabbing' : 'grab'}
        onMouseDown={handleMouseDown}
        userSelect="none"
      >
        <Button
          size="sm"
          backgroundColor="#FB6174"
          color="white"
          borderRadius="full"
          width="60px"
          height="60px"
          _hover={{ filter: 'brightness(0.9)' }}
          _active={{ filter: 'brightness(0.8)' }}
          boxShadow="lg"
          onClick={handleClick}
        >
          <Text fontSize="xs" fontWeight="bold">
            DEBUG
          </Text>
        </Button>
      </Box>
    </Portal>
  )
}
